import React from 'react';
import { Link } from 'react-router-dom';
import { openWhatsApp } from '../utils/whatsapp';
import { useCart } from '../context/CartContext';
import '../styles/ProductCard.css';

const parseEnvBoolean = (value, fallback = true) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return fallback;
};

const ProductCard = ({ product, showActions = true }) => {
  const { addToCart } = useCart();

  // Leer configuración desde variables de entorno
  const showAddToCartButton = parseEnvBoolean(
    process.env.REACT_APP_SHOW_ADD_TO_CART_BUTTON,
    true
  );
  const showWhatsAppButton = parseEnvBoolean(
    process.env.REACT_APP_SHOW_WHATSAPP_BUTTON,
    true
  );
  
  // Verificar si el producto tiene precio válido
  const hasValidPrice = product.precio && product.precio > 0;
  
  // Combinar showActions con las variables de entorno y validación de precio
  const shouldShowAddToCart = showActions && showAddToCartButton && hasValidPrice;
  const shouldShowWhatsApp = showActions && showWhatsAppButton;
  const shouldShowActions = showActions && (shouldShowAddToCart || showWhatsAppButton);

  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    openWhatsApp(product);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    addToCart(product);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(price);
  };

  // Obtener la primera imagen del array o usar la propiedad imagen (backward compatible)
  const getProductImage = () => {
    // Primero intentar con el array de imágenes
    if (product.imagenes && Array.isArray(product.imagenes) && product.imagenes.length > 0) {
      // Filtrar URLs vacías o inválidas
      const validImage = product.imagenes.find(img => img && img.trim() !== '');
      if (validImage) {
        return validImage;
      }
    }
    
    // Luego intentar con la propiedad imagen individual
    if (product.imagen && product.imagen.trim() !== '') {
      return product.imagen;
    }
    
    
    // Si no hay imagen válida, usar placeholder
    return 'https://placehold.co/400x300?text=Sin+Imagen';
  };

  return (
    <div className="product-card fade-in">
      <Link to={`/producto/${product.id}`} className="product-image-link">
        <div className="product-image-container">
          <img 
            src={getProductImage()} 
            alt={product.descripcion}
            className="product-image"
            loading="lazy"
            onError={(e) => {
              e.target.src = 'https://placehold.co/400x300?text=Sin+Imagen';
            }}
          />
          {product.existencia > 0 ? (
            <span className="badge badge-success product-badge">
              En Stock
            </span>
          ) : (
            <span className="badge badge-error product-badge">
              Sin Stock
            </span>
          )}
          {/* Indicador de múltiples imágenes */}
          {(() => {
            const validImages = product.imagenes && Array.isArray(product.imagenes) 
              ? product.imagenes.filter(img => img && img.trim() !== '')
              : [];
            
            return validImages.length > 1 && (
              <span className="badge product-images-count">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 4h16v12H4z" opacity="0.3"/>
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                </svg>
                {validImages.length}
              </span>
            );
          })()}
        </div>
      </Link>
      
      <div className="product-content">
        <Link to={`/producto/${product.id}`} className="product-title-link">
          <h3 className="product-title">{product.descripcion}</h3>
        </Link>
        
        {product.categoria && (
          <p className="product-category">{product.categoria}</p>
        )}
        
        <div className="product-footer">
          <div className="product-price-section">
            {hasValidPrice ? (
              <span className="product-price">{formatPrice(product.precio)}</span>
            ) : (
              <span className="product-price-consult">Consultar precio</span>
            )}
            {product.existencia > 0 && (
              <span className="product-stock">Stock: {product.existencia}</span>
            )}
          </div>
          
          {shouldShowActions && (
            <div className="product-actions">
              {shouldShowAddToCart && (
                <button 
                  className="btn btn-primary btn-add-to-cart"
                  onClick={handleAddToCart}
                  disabled={!product.existencia || product.existencia === 0}
                  title="Agregar al carrito"
                >
                  🛒 Agregar
                </button>
              )}
              
              {shouldShowWhatsApp && (
                <button 
                  className="btn btn-whatsapp btn-product-whatsapp"
                  onClick={handleWhatsAppClick}
                  title="Consultar por WhatsApp"
                >
                  <svg 
                    width="18" 
                    height="18" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
